import React, { useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    Drawer,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import type { ICampaign, ICampaignSampleVideo } from '../../../redux/buzzbridge/types';
import {
    campaignSampleVideoSignedUrl,
    campaignSampleVideoUrl,
    campaignSampleVideoDeleteUrl,
} from '../../../configurations/api/url';
import { getFileExtension } from '../../../utils/AppUtils';
import { store } from '../../../redux/store';
import { VideoModalPlayer } from './VideoModalPlayer';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER,
} from '../brandDashboardTokens';
import { FONT_ENUM } from '../../../types/global/fonts.types';

/* ── Video Card ──────────────────────────────────────── */

function VideoCard({
    video,
    deleting,
    onDelete,
    onPlay,
}: {
    video: ICampaignSampleVideo;
    deleting: boolean;
    onDelete: () => void;
    onPlay: () => void;
}) {
    const [errored, setErrored] = useState(false);

    return (
        <Box
            sx={{
                position: 'relative',
                borderRadius: 2,
                bgcolor: '#000',
                border: '1px solid rgba(255,255,255,0.08)',
                aspectRatio: '16/9',
                cursor: 'pointer',
                '&:hover .play-overlay': { opacity: 1 },
            }}
            onClick={onPlay}>
            {errored ? (
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#111',
                        borderRadius: 2,
                    }}>
                    <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', textAlign: 'center', px: 2 }}>
                        Unable to load preview
                    </Typography>
                </Box>
            ) : (
                <video
                    key={video._id}
                    src={video.url}
                    preload="metadata"
                    onError={() => setErrored(true)}
                    style={{
                        width: '100%',
                        height: '100%',
                        display: 'block',
                        objectFit: 'cover',
                        backgroundColor: '#000',
                        borderRadius: 8,
                        pointerEvents: 'none',
                    }}
                />
            )}

            <Box
                className="play-overlay"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(0,0,0,0.45)',
                    borderRadius: 2,
                    opacity: 0,
                    transition: 'opacity 0.15s',
                }}>
                <PlayCircleIcon sx={{ fontSize: 40, color: '#fff' }} />
            </Box>

            <Tooltip title="Delete video">
                <IconButton
                    size="small"
                    disabled={deleting}
                    onClick={e => {
                        e.stopPropagation();
                        onDelete();
                    }}
                    sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        bgcolor: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                        '&:hover': { bgcolor: 'rgba(200,0,0,0.75)' },
                        zIndex: 1,
                    }}>
                    {deleting ? (
                        <CircularProgress size={14} sx={{ color: '#fff' }} />
                    ) : (
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    )}
                </IconButton>
            </Tooltip>
        </Box>
    );
}

/* ── Panel ───────────────────────────────────────────── */

interface Props {
    open: boolean;
    onClose: () => void;
    campaign: ICampaign;
}

export function CampaignSampleVideosPanel({ open, onClose, campaign }: Props) {
    const [videos, setVideos] = useState<ICampaignSampleVideo[]>([...(campaign.sample_videos ?? [])]);
    const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Sync when panel reopens with fresh campaign data
    React.useEffect(() => {
        if (open) setVideos([...(campaign.sample_videos ?? [])]);
    }, [open, campaign.sample_videos]);

    const authHeader = () => {
        const token: string = (store.getState() as any).authentication?.token ?? '';
        return token ? `Bearer ${token}` : '';
    };

    const handleDelete = async (video: ICampaignSampleVideo) => {
        setDeletingIds(prev => new Set(prev).add(video._id));
        try {
            await fetch(campaignSampleVideoDeleteUrl(campaign._id, video._id), {
                method: 'DELETE',
                headers: { Authorization: authHeader() },
            });
            setVideos(prev => prev.filter(v => v._id !== video._id));
        } catch {
            // silently ignore
        } finally {
            setDeletingIds(prev => {
                const next = new Set(prev);
                next.delete(video._id);
                return next;
            });
        }
    };

    const handleAddFiles = async (files: File[]) => {
        const videoFiles = files.filter(f => f.type.startsWith('video/'));
        if (!videoFiles.length) return;
        setUploading(true);
        for (const file of videoFiles) {
            try {
                const ext = getFileExtension(file);
                const signedRes = await fetch(campaignSampleVideoSignedUrl(campaign._id, ext), {
                    method: 'GET',
                    headers: { Authorization: authHeader(), 'Content-Type': 'application/json' },
                });
                if (!signedRes.ok) continue;
                const signedJson = await signedRes.json();
                const signedUrl: string = signedJson?.data?.url;
                const videoKey: string = signedJson?.data?.videoKey;
                if (!signedUrl || !videoKey) continue;

                await fetch(signedUrl, {
                    method: 'PUT',
                    body: file,
                    headers: { 'Content-Type': file.type },
                });

                const patchRes = await fetch(campaignSampleVideoUrl(campaign._id), {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', Authorization: authHeader() },
                    body: JSON.stringify({ url: videoKey }),
                });
                if (patchRes.ok) {
                    const patchJson = await patchRes.json();
                    const added: ICampaignSampleVideo | undefined =
                        patchJson?.data?.sample_videos?.slice(-1)[0];
                    if (added) setVideos(prev => [...prev, added]);
                }
            } catch {
                // skip failed file
            }
        }
        setUploading(false);
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100vw', sm: 540 },
                    bgcolor: '#111',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 2.5,
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, color: '#fff' }}>
                    Sample Videos
                </Typography>
                <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Body */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 11,
                            letterSpacing: '0.14em',
                            textTransform: 'uppercase',
                            color: '#fff',
                            mb: 0.75,
                        }}>
                        Sample Videos
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', mb: 2 }}>
                        Reference videos creators can see when browsing your campaign. Changes apply immediately.
                    </Typography>

                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        hidden
                        accept="video/*"
                        onChange={e => {
                            handleAddFiles(Array.from(e.target.files ?? []));
                            e.target.value = '';
                        }}
                    />

                    {videos.length > 0 && (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                gap: 1.5,
                                mb: 2,
                            }}>
                            {videos.map(v => (
                                <VideoCard
                                    key={v._id}
                                    video={v}
                                    deleting={deletingIds.has(v._id)}
                                    onDelete={() => handleDelete(v)}
                                    onPlay={() => setActiveVideoUrl(v.url)}
                                />
                            ))}
                        </Box>
                    )}

                    <Box
                        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={e => {
                            e.preventDefault();
                            setDragOver(false);
                            handleAddFiles(Array.from(e.dataTransfer.files));
                        }}
                        onClick={() => !uploading && fileInputRef.current?.click()}
                        sx={{
                            border: `2px dashed ${dragOver ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.12)'}`,
                            borderRadius: 2,
                            py: 3,
                            px: 2,
                            textAlign: 'center',
                            cursor: uploading ? 'default' : 'pointer',
                            transition: 'border-color 0.2s',
                            bgcolor: dragOver ? 'rgba(255,201,101,0.04)' : 'transparent',
                            '&:hover': !uploading ? { borderColor: BRAND_LUXE_PRIMARY } : {},
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                        }}>
                        {uploading ? (
                            <>
                                <CircularProgress size={16} sx={{ color: BRAND_LUXE_PRIMARY }} />
                                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                                    Uploading…
                                </Typography>
                            </>
                        ) : (
                            <>
                                <AddCircleOutlineIcon sx={{ fontSize: 18, color: BRAND_LUXE_PRIMARY }} />
                                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                                    Add videos · drag & drop or click · MP4, MOV, WEBM
                                </Typography>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Footer — no save needed, changes apply immediately */}
            <Box
                sx={{
                    px: 3,
                    py: 2.5,
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    bgcolor: `${BRAND_SURFACE_CONTAINER}80`,
                    display: 'flex',
                    justifyContent: 'flex-end',
                }}>
                <Button
                    onClick={onClose}
                    sx={{
                        px: 4,
                        py: 1.5,
                        bgcolor: BRAND_LUXE_PRIMARY,
                        color: '#000',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        borderRadius: 3,
                        '&:hover': { bgcolor: '#e6b500' },
                    }}>
                    Done
                </Button>
            </Box>

            {/* Video player */}
            <Dialog
                open={!!activeVideoUrl}
                onClose={() => setActiveVideoUrl(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { bgcolor: '#000', borderRadius: 3 } }}>
                {activeVideoUrl && (
                    <VideoModalPlayer
                        src={activeVideoUrl}
                        onClose={() => setActiveVideoUrl(null)}
                        maxVideoHeight="75vh"
                    />
                )}
            </Dialog>
        </Drawer>
    );
}
